// =============================================================================
// Module Project - useCollaborationStore (Phase 6A - Permission-based)
// Gestion des membres projet via le nouveau modèle permission_level
// N'importe PAS supabase - utilise uniquement les repositories
// =============================================================================

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { TaskAssigneeInfo, OrgMemberInfo } from '../data/collaborationRepository';
import * as collabRepo from '../data/collaborationRepository';
import * as permRepo from '../data/permissionRepository';
import type { 
  ProjectMemberInfo, 
  PermissionLevel, 
  ImplicitAdminInfo,
  PermissionSource,
} from '../data/permissionRepository';
import { executeAutomations } from '../data/automationExecutor';
import { 
  notifyTaskAssigned, 
  notifyTaskUnassigned, 
  notifyProjectMemberAdded,
  notifyProjectMemberRemoved,
  getTaskInfo,
} from '../data/notificationService';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface CollaborationState {
  members: ProjectMemberInfo[];
  implicitAdmins: ImplicitAdminInfo[];
  assignees: TaskAssigneeInfo[];
  orgMembers: OrgMemberInfo[];
  loadingOrgMembers: boolean;
  loading: boolean;
  currentUserMemberId: string | null;
  currentUserPermission: PermissionLevel | null;
  currentUserSource: PermissionSource | null;
}
type CollaborationAction =
  | { type: 'SET_MEMBERS'; payload: { members: ProjectMemberInfo[]; implicitAdmins: ImplicitAdminInfo[] } }
  | { type: 'ADD_MEMBER'; payload: ProjectMemberInfo }
  | { type: 'UPDATE_MEMBER_PERMISSION'; payload: { org_member_id: string; permission: PermissionLevel } }
  | { type: 'REMOVE_MEMBER'; payload: string }
  | { type: 'SET_ASSIGNEES'; payload: TaskAssigneeInfo[] }
  | { type: 'ASSIGN_TASK'; payload: TaskAssigneeInfo[] }
  | { type: 'UNASSIGN_TASK'; payload: { task_id: string; org_member_id: string } }
  | { type: 'SET_CURRENT_USER'; payload: { memberId: string; permission: PermissionLevel; source: PermissionSource } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ORG_MEMBERS'; payload: OrgMemberInfo[] }
  | { type: 'SET_LOADING_ORG_MEMBERS'; payload: boolean }
  | { type: 'ROLLBACK_MEMBERS'; payload: ProjectMemberInfo[] }
  | { type: 'ROLLBACK_ASSIGNEES'; payload: TaskAssigneeInfo[] }
  | { type: 'CLEAR_CURRENT_USER' }
  | { type: 'RESET_PROJECT_SCOPE' };

const initialState: CollaborationState = {
  members: [],
  implicitAdmins: [],
  assignees: [],
  orgMembers: [],
  loadingOrgMembers: false,
  loading: true,
  currentUserMemberId: null,
  currentUserPermission: null,
  currentUserSource: null,
};

function collaborationReducer(state: CollaborationState, action: CollaborationAction): CollaborationState {
  switch (action.type) {
    case 'SET_MEMBERS':
      return {
        ...state,
        members: action.payload.members,
        implicitAdmins: action.payload.implicitAdmins,
        loading: false,
      };

    case 'ADD_MEMBER':
      return { ...state, members: [...state.members, action.payload] };

    case 'UPDATE_MEMBER_PERMISSION':
      return {
        ...state,
        members: state.members.map((m) =>
          m.org_member_id === action.payload.org_member_id
            ? { ...m, effective_permission: action.payload.permission }
            : m
        ),
      };

    case 'REMOVE_MEMBER':
      return {
        ...state,
        members: state.members.filter((m) => m.org_member_id !== action.payload),
        assignees: state.assignees.filter((a) => a.org_member_id !== action.payload),
      };

    case 'SET_ASSIGNEES':
      return { ...state, assignees: action.payload };

    case 'ASSIGN_TASK':
      return {
        ...state,
        assignees: [
          ...state.assignees,
          ...action.payload.filter(
            (newA) =>
              !state.assignees.some(
                (a) => a.task_id === newA.task_id && a.org_member_id === newA.org_member_id
              )
          ),
        ],
      };

    case 'UNASSIGN_TASK':
      return {
        ...state,
        assignees: state.assignees.filter(
          (a) => !(a.task_id === action.payload.task_id && a.org_member_id === action.payload.org_member_id)
        ),
      };

    case 'SET_CURRENT_USER':
      return {
        ...state,
        currentUserMemberId: action.payload.memberId,
        currentUserPermission: action.payload.permission,
        currentUserSource: action.payload.source,
      };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'ROLLBACK_MEMBERS':
      return { ...state, members: action.payload };

    case 'ROLLBACK_ASSIGNEES':
      return { ...state, assignees: action.payload };

    case 'CLEAR_CURRENT_USER':
      return {
        ...state,
        currentUserMemberId: null,
        currentUserPermission: null,
        currentUserSource: null,
      };

    case 'RESET_PROJECT_SCOPE':
      return {
        ...state,
        members: [],
        implicitAdmins: [],
        assignees: [],
        loading: true,
        currentUserMemberId: null,
        currentUserPermission: null,
        currentUserSource: null,
      };

    case 'SET_ORG_MEMBERS':
      return { ...state, orgMembers: action.payload, loadingOrgMembers: false };

    case 'SET_LOADING_ORG_MEMBERS':
      return { ...state, loadingOrgMembers: action.payload };

    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────
export function useCollaborationStore(
  projectId: string | undefined,
  orgId: string | undefined,
  userId: string | undefined
) {
  const [state, dispatch] = useReducer(collaborationReducer, initialState);
  const isMountedRef = useRef(true);

  // Track mounted state to prevent dispatch after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // LOAD MEMBERS & ASSIGNEES (using new permission model)
  // ─────────────────────────────────────────────────────────────────────────────
  const loadMembers = useCallback(async () => {
    if (!projectId || !orgId) {
      if (isMountedRef.current) {
        dispatch({ type: 'RESET_PROJECT_SCOPE' });
        dispatch({ type: 'SET_LOADING', payload: false });
      }
      return;
    }

    if (isMountedRef.current) {
      dispatch({ type: 'SET_LOADING', payload: true });
    }

    try {
      const [members, assignees] = await Promise.all([
        permRepo.fetchProjectMembers(projectId),
        collabRepo.fetchTaskAssignees(projectId),
      ]);

      // Fetch implicit admins (org-level owners/admins not in explicit list)
      const explicitMemberIds = members.map(m => m.org_member_id);
      const implicitAdmins = await permRepo.fetchImplicitAdmins(orgId, explicitMemberIds);

      if (isMountedRef.current) {
        dispatch({ type: 'SET_MEMBERS', payload: { members, implicitAdmins } });
        dispatch({ type: 'SET_ASSIGNEES', payload: assignees });

        // Find current user's member info
        if (userId) {
          const currentMember = members.find((m) => m.user_id === userId);
          const implicitAdmin = implicitAdmins.find((a) => a.user_id === userId);

          if (currentMember) {
            const isAlsoOrgAdmin = implicitAdmin != null || await permRepo.isOrgAdmin(orgId!, userId);

            dispatch({
              type: 'SET_CURRENT_USER',
              payload: {
                memberId: currentMember.org_member_id,
                permission: isAlsoOrgAdmin ? 'full' : currentMember.effective_permission,
                source: isAlsoOrgAdmin ? 'org' : currentMember.source,
              },
            });
          } else if (implicitAdmin) {
            dispatch({
              type: 'SET_CURRENT_USER',
              payload: {
                memberId: implicitAdmin.org_member_id,
                permission: 'full',
                source: 'org',
              },
            });
          } else {
            // Expected when the current user accesses the project via space/workspace
            // inheritance rather than an explicit project membership. Fall back to RPC
            // resolution silently (no warning to keep the console clean).
            try {
              const fallbackPerm = await permRepo.getCurrentUserPermission(projectId!, userId);

              if (isMountedRef.current) {
                if (fallbackPerm) {
                  // Resolve the real org_member_id so downstream writes
                  // (comments, assignments, …) don't receive the literal
                  // string 'fallback' as a UUID (causes Postgres 22P02).
                  let resolvedMemberId: string | null = null;
                  try {
                    const { data: omRow } = await (supabase as any)
                      .from('core_organization_members')
                      .select('id')
                      .eq('org_id', orgId!)
                      .eq('user_id', userId)
                      .maybeSingle();
                    resolvedMemberId = (omRow as { id?: string } | null)?.id ?? null;
                  } catch (lookupErr) {
                    console.error('[useCollaborationStore] org_member_id lookup failed:', lookupErr);
                  }

                  if (!resolvedMemberId) {
                    dispatch({ type: 'CLEAR_CURRENT_USER' });
                    return;
                  }

                  dispatch({
                    type: 'SET_CURRENT_USER',
                    payload: {
                      memberId: resolvedMemberId,
                      permission: fallbackPerm,
                      source: 'project',
                    },
                  });
                } else {
                  dispatch({ type: 'CLEAR_CURRENT_USER' });
                }
              }
            } catch (fallbackErr) {
              console.error('[useCollaborationStore] RPC fallback failed:', fallbackErr);
              if (isMountedRef.current) {
                dispatch({ type: 'CLEAR_CURRENT_USER' });
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('Error loading collaboration data:', err);
    } finally {
      if (isMountedRef.current) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, [projectId, orgId, userId]);

  useEffect(() => {
    dispatch({ type: 'RESET_PROJECT_SCOPE' });
  }, [projectId, orgId, userId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  // Listen to assignee:added and assignee:removed events (from WorkloadStore) to refresh assignees
  useEffect(() => {
    const handleAssigneeChanged = (event: CustomEvent<{ taskId: string; memberId: string }>) => {
      console.log('[useCollaborationStore] Assignee changed externally, refreshing...', event.detail);
      // Reload assignees from server to sync with WorkloadStore changes
      if (projectId && orgId) {
        collabRepo.fetchTaskAssignees(projectId).then((assignees) => {
          if (isMountedRef.current) {
            dispatch({ type: 'SET_ASSIGNEES', payload: assignees });
          }
        }).catch(console.error);
      }
    };

    window.addEventListener('assignee:added', handleAssigneeChanged as EventListener);
    window.addEventListener('assignee:removed', handleAssigneeChanged as EventListener);
    return () => {
      window.removeEventListener('assignee:added', handleAssigneeChanged as EventListener);
      window.removeEventListener('assignee:removed', handleAssigneeChanged as EventListener);
    };
  }, [projectId, orgId]);

  // ─────────────────────────────────────────────────────────────────────────────
  // REALTIME: Listen to project_permissions changes to auto-sync members
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;

    const channel = supabase
      .channel(`collab-perms-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_permissions',
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          console.log('[useCollaborationStore] project_permissions changed, refreshing members...');
          loadMembers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, loadMembers]);

  // ─────────────────────────────────────────────────────────────────────────────
  // LOAD ORG MEMBERS (for dialog)
  // ─────────────────────────────────────────────────────────────────────────────
  const loadOrgMembers = useCallback(async () => {
    if (!orgId) return;
    
    dispatch({ type: 'SET_LOADING_ORG_MEMBERS', payload: true });
    try {
      const orgMembers = await collabRepo.fetchOrganizationMembers(orgId);
      dispatch({ type: 'SET_ORG_MEMBERS', payload: orgMembers });
    } catch (err) {
      console.error('Error loading org members:', err);
      dispatch({ type: 'SET_LOADING_ORG_MEMBERS', payload: false });
    }
  }, [orgId]);

  const getTaskAssignees = useCallback(
    (taskId: string): ProjectMemberInfo[] => {
      const assigneeIds = state.assignees.filter((a) => a.task_id === taskId).map((a) => a.org_member_id);
      // Search in both explicit members and implicit admins
      const allMembers: ProjectMemberInfo[] = [...state.members];
      const existingIds = new Set(allMembers.map(m => m.org_member_id));
      for (const admin of state.implicitAdmins) {
        if (!existingIds.has(admin.org_member_id)) {
          allMembers.push({
            org_member_id: admin.org_member_id,
            user_id: admin.user_id,
            effective_permission: 'full',
            source: 'project',
            full_name: admin.full_name,
            email: admin.email,
            avatar_url: admin.avatar_url,
            has_space_access: false,
          });
        }
      }
      return allMembers.filter((m) => assigneeIds.includes(m.org_member_id));
    },
    [state.assignees, state.members, state.implicitAdmins]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // IS ASSIGNED
  // ─────────────────────────────────────────────────────────────────────────────
  const isAssigned = useCallback(
    (taskId: string, orgMemberId: string): boolean => {
      return state.assignees.some((a) => a.task_id === taskId && a.org_member_id === orgMemberId);
    },
    [state.assignees]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // ASSIGN TASK
  // ─────────────────────────────────────────────────────────────────────────────
  const assignTask = useCallback(
    async (taskId: string, orgMemberIds: string[], onTaskUpdated?: () => void, actorUserId?: string): Promise<{ automationsExecuted: number }> => {
      if (!orgId || !projectId) return { automationsExecuted: 0 };

      const originalAssignees = [...state.assignees];
      // Find which members are truly new (not already assigned)
      const existingMemberIds = state.assignees
        .filter(a => a.task_id === taskId)
        .map(a => a.org_member_id);
      const newMemberIds = orgMemberIds.filter(id => !existingMemberIds.includes(id));
      
      const newAssignees = orgMemberIds.map((id) => ({ task_id: taskId, org_member_id: id }));

      dispatch({ type: 'ASSIGN_TASK', payload: newAssignees });

      let totalAutomationsExecuted = 0;

      try {
        await collabRepo.assignTask(orgId, taskId, orgMemberIds);
        
        // Get task info for notifications
        const taskInfo = await getTaskInfo(taskId);
        
        // Send notifications for each newly added assignee
        for (const memberId of newMemberIds) {
          // Execute automations
          try {
            const result = await executeAutomations(
              orgId,
              projectId,
              taskId,
              'on_assignee_added',
              { assignee_id: memberId }
            );
            totalAutomationsExecuted += result.executed;
            if (result.executed > 0) {
              console.log(`[useCollaborationStore] Executed ${result.executed} automations for assignee ${memberId}`);
            }
          } catch (err) {
            console.error('[useCollaborationStore] Automation execution error:', err);
          }
          
          // Send notification
          if (taskInfo) {
            notifyTaskAssigned({
              orgId,
              projectId,
              taskId,
              taskTitle: taskInfo.title,
              assigneeMemberId: memberId,
              actorUserId,
            }).catch(console.error);
          }
        }
        
        // Notify parent if automations were executed (task data may have changed)
        if (totalAutomationsExecuted > 0 && onTaskUpdated) {
          onTaskUpdated();
        }
        
        return { automationsExecuted: totalAutomationsExecuted };
      } catch (err: any) {
        console.error('Assign task failed, rolling back:', err);
        dispatch({ type: 'ROLLBACK_ASSIGNEES', payload: originalAssignees });
        toast.error(err.message || "Erreur lors de l'assignation");
        return { automationsExecuted: 0 };
      }
    },
    [orgId, projectId, state.assignees]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // UNASSIGN TASK
  // ─────────────────────────────────────────────────────────────────────────────
  const unassignTask = useCallback(
    async (taskId: string, orgMemberId: string, actorUserId?: string) => {
      if (!orgId || !projectId) return;

      const originalAssignees = [...state.assignees];

      dispatch({ type: 'UNASSIGN_TASK', payload: { task_id: taskId, org_member_id: orgMemberId } });

      try {
        await collabRepo.unassignTask(orgId, taskId, orgMemberId);
        
        // Send unassign notification
        const taskInfo = await getTaskInfo(taskId);
        if (taskInfo) {
          notifyTaskUnassigned({
            orgId,
            projectId,
            taskId,
            taskTitle: taskInfo.title,
            unassignedMemberId: orgMemberId,
            actorUserId,
          }).catch(console.error);
        }
      } catch (err: any) {
        console.error('Unassign task failed, rolling back:', err);
        dispatch({ type: 'ROLLBACK_ASSIGNEES', payload: originalAssignees });
        toast.error(err.message || 'Erreur lors de la désassignation');
      }
    },
    [orgId, projectId, state.assignees]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // TOGGLE ASSIGNEE
  // ─────────────────────────────────────────────────────────────────────────────
  const toggleAssignee = useCallback(
    async (taskId: string, orgMemberId: string, onTaskUpdated?: () => void): Promise<{ automationsExecuted: number }> => {
      if (isAssigned(taskId, orgMemberId)) {
        await unassignTask(taskId, orgMemberId);
        return { automationsExecuted: 0 };
      } else {
        return await assignTask(taskId, [orgMemberId], onTaskUpdated);
      }
    },
    [isAssigned, assignTask, unassignTask]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // ASSIGN SELF
  // ─────────────────────────────────────────────────────────────────────────────
  const assignSelf = useCallback(
    async (taskId: string) => {
      if (!state.currentUserMemberId) return;
      await assignTask(taskId, [state.currentUserMemberId]);
    },
    [state.currentUserMemberId, assignTask]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // REMOVE ALL ASSIGNEES
  // ─────────────────────────────────────────────────────────────────────────────
  const removeAllAssignees = useCallback(
    async (taskId: string) => {
      const taskAssignees = state.assignees.filter((a) => a.task_id === taskId);
      for (const assignee of taskAssignees) {
        await unassignTask(taskId, assignee.org_member_id);
      }
    },
    [state.assignees, unassignTask]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // BATCH UPDATE ASSIGNEES - Single API call for multiple add/remove
  // ─────────────────────────────────────────────────────────────────────────────
  const batchUpdateAssignees = useCallback(
    async (taskId: string, addMemberIds: string[], removeMemberIds: string[], actorUserId?: string): Promise<void> => {
      if (!orgId || !projectId) return;

      const originalAssignees = [...state.assignees];

      // Optimistic update - remove
      for (const memberId of removeMemberIds) {
        dispatch({ type: 'UNASSIGN_TASK', payload: { task_id: taskId, org_member_id: memberId } });
      }
      
      // Optimistic update - add
      const newAssignees = addMemberIds.map((id) => ({ task_id: taskId, org_member_id: id }));
      dispatch({ type: 'ASSIGN_TASK', payload: newAssignees });

      try {
        await collabRepo.batchUpdateAssignees(orgId, taskId, addMemberIds, removeMemberIds);
        
        // Get task info for notifications
        const taskInfo = await getTaskInfo(taskId);
        
        // Execute automations and send notifications for newly added assignees
        if (addMemberIds.length > 0) {
          for (const memberId of addMemberIds) {
            try {
              await executeAutomations(
                orgId,
                projectId,
                taskId,
                'on_assignee_added',
                { assignee_id: memberId }
              );
            } catch (err) {
              console.error('[useCollaborationStore] Automation execution error:', err);
            }
            
            // Send notification
            if (taskInfo) {
              notifyTaskAssigned({
                orgId,
                projectId,
                taskId,
                taskTitle: taskInfo.title,
                assigneeMemberId: memberId,
                actorUserId,
              }).catch(console.error);
            }
          }
        }
        
        // Send notifications for removed assignees
        if (removeMemberIds.length > 0 && taskInfo) {
          for (const memberId of removeMemberIds) {
            notifyTaskUnassigned({
              orgId,
              projectId,
              taskId,
              taskTitle: taskInfo.title,
              unassignedMemberId: memberId,
              actorUserId,
            }).catch(console.error);
          }
        }
      } catch (err: any) {
        console.error('Batch update assignees failed, rolling back:', err);
        dispatch({ type: 'ROLLBACK_ASSIGNEES', payload: originalAssignees });
        toast.error(err.message || "Erreur lors de la mise à jour des assignations");
        throw err;
      }
    },
    [orgId, projectId, state.assignees]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // ADD MEMBER (using new permission model)
  // ─────────────────────────────────────────────────────────────────────────────
  const addMember = useCallback(
    async (orgMemberId: string, permission: PermissionLevel = 'view') => {
      if (!projectId || !orgId) return;

      try {
        await permRepo.addProjectPermission(projectId, orgMemberId, permission);
        await loadMembers();
        toast.success('Membre ajouté au projet');
      } catch (err: any) {
        console.error('Add member failed:', err);
        toast.error(err.message || "Erreur lors de l'ajout du membre");
      }
    },
    [projectId, orgId, loadMembers]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // UPDATE MEMBER PERMISSION (using new permission model)
  // ─────────────────────────────────────────────────────────────────────────────
  const updateMemberPermission = useCallback(
    async (orgMemberId: string, newPermission: PermissionLevel) => {
      if (!projectId || !orgId) return;

      const originalMembers = [...state.members];

      dispatch({ type: 'UPDATE_MEMBER_PERMISSION', payload: { org_member_id: orgMemberId, permission: newPermission } });

      try {
        await permRepo.updateProjectPermission(orgId, projectId, orgMemberId, newPermission);
        toast.success('Permission mise à jour');
      } catch (err: any) {
        console.error('Update member permission failed, rolling back:', err);
        dispatch({ type: 'ROLLBACK_MEMBERS', payload: originalMembers });
        toast.error(err.message || 'Erreur lors de la mise à jour de la permission');
      }
    },
    [projectId, orgId, state.members]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // REMOVE MEMBER (using new permission model)
  // ─────────────────────────────────────────────────────────────────────────────
  const removeMember = useCallback(
    async (orgMemberId: string) => {
      if (!projectId || !orgId) return;

      const originalMembers = [...state.members];
      const originalAssignees = [...state.assignees];

      dispatch({ type: 'REMOVE_MEMBER', payload: orgMemberId });

      try {
        await permRepo.removeProjectPermission(orgId, projectId, orgMemberId);
        toast.success('Membre retiré du projet');
      } catch (err: any) {
        console.error('Remove member failed, rolling back:', err);
        dispatch({ type: 'ROLLBACK_MEMBERS', payload: originalMembers });
        dispatch({ type: 'ROLLBACK_ASSIGNEES', payload: originalAssignees });
        toast.error(err.message || 'Erreur lors du retrait du membre');
      }
    },
    [projectId, orgId, state.members, state.assignees]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // OVERRIDE INHERITED PERMISSION (create project_permissions for space member)
  // ─────────────────────────────────────────────────────────────────────────────
  const overridePermission = useCallback(
    async (orgMemberId: string, permission: PermissionLevel) => {
      if (!projectId || !orgId) return;

      try {
        await permRepo.addProjectPermission(projectId, orgMemberId, permission);
        await loadMembers(); // Reload to get updated source
        toast.success('Override créé - permission projet appliquée');
      } catch (err: any) {
        console.error('Override permission failed:', err);
        toast.error(err.message || "Erreur lors de la création de l'override");
      }
    },
    [projectId, orgId, loadMembers]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // ROLLBACK OVERRIDE (delete project_permissions to revert to space inheritance)
  // ─────────────────────────────────────────────────────────────────────────────
  const rollbackOverride = useCallback(
    async (orgMemberId: string) => {
      if (!projectId || !orgId) return;

      try {
        await permRepo.removeProjectPermission(orgId, projectId, orgMemberId);
        await loadMembers(); // Reload to get space-inherited permission
        toast.success('Override supprimé - retour à l\'héritage Space');
      } catch (err: any) {
        console.error('Rollback override failed:', err);
        toast.error(err.message || 'Erreur lors de la suppression de l\'override');
      }
    },
    [projectId, orgId, loadMembers]
  );
  // Permission checks based on effective_permission
  // Owner/Admin org bypass: if source is 'org', always grant full capabilities
  // ─────────────────────────────────────────────────────────────────────────────
  const isOrgLevelAdmin = state.currentUserSource === 'org';
  const effectivePermission = isOrgLevelAdmin ? 'full' as permRepo.PermissionLevel : state.currentUserPermission;
  const capabilities = permRepo.getCapabilities(effectivePermission);
  
  const canManageMembers = capabilities.canManageMembers;
  const canWrite = capabilities.canEdit;
  const canEdit = capabilities.canEdit;
  const isViewer = !isOrgLevelAdmin && state.currentUserPermission === 'view';
  
  // Configurable permissions now derive from permission level
  // Owner/Admin always can comment/edit/etc.
  // Comment-level users can: comment, add attachments, watch tasks
  // but CANNOT: modify task fields (status, priority, dates, assignees, etc.)
  // canLogTime requires edit permission (RLS: can_edit_project)
  const canComment = capabilities.canComment;
  const canAddAttachments = capabilities.canComment;
  const canLogTime = capabilities.canEdit;
  const canWatchTasks = capabilities.canRead;

  // Computed permission helpers
  const hasFull = isOrgLevelAdmin || state.currentUserPermission === 'full';
  const hasEdit = state.currentUserPermission === 'edit' || hasFull;
  const hasView = state.currentUserPermission === 'view' || hasEdit;

  // Legacy compatibility aliases
  const isProjectLead = hasFull;
  const isOwnerOrAdmin = hasFull;

  // Legacy role getter for backward compatibility
  const currentUserProjectRole = state.currentUserPermission === 'full' 
    ? 'project_lead' 
    : state.currentUserPermission === 'edit' 
      ? 'contributor' 
      : 'viewer';

  // Seuls les membres explicitement ajoutés au projet sont assignables et
  // apparaissent dans les vues projet (Gantt, Échéancier, Charge, Disponibilité,
  // Charge Planning, Gantt Équipe…). Les owners/admins du workspace gardent
  // leur accès complet implicite (lecture/écriture/admin) mais doivent être
  // ajoutés explicitement pour figurer dans ces listes et être assignables.
  const allAssignableMembers: ProjectMemberInfo[] = useMemo(
    () => state.members,
    [state.members]
  );

  return {
    ...state,
    // All members including implicit admins (for assignee selectors)
    allAssignableMembers,
    // Legacy compatibility - map permission to role
    currentUserProjectRole,
    getTaskAssignees,
    isAssigned,
    assignTask,
    unassignTask,
    toggleAssignee,
    assignSelf,
    removeAllAssignees,
    batchUpdateAssignees,
    addMember,
    updateMemberPermission,
    removeMember,
    overridePermission,
    rollbackOverride,
    // Legacy alias
    updateMemberRole: updateMemberPermission,
    canManageMembers,
    canWrite,
    canEdit,
    isViewer,
    isProjectLead,
    isOwnerOrAdmin,
    hasFull,
    hasEdit,
    effectivePermission,
    hasView,
    canComment,
    canAddAttachments,
    canLogTime,
    canWatchTasks,
    loadOrgMembers,
    refresh: loadMembers,
  };
}

// Re-export types
export type { ProjectMemberInfo, PermissionLevel, ImplicitAdminInfo, PermissionSource } from '../data/permissionRepository';
export type { TaskAssigneeInfo, OrgMemberInfo } from '../data/collaborationRepository';
